import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { switchMap, tap } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { IntegrationKinds, OfferPromoteJobBoardsHotspot } from '@core/integrations-types';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
} from '@core/user-interface-legacy';

import {
  CurrentHotspotToken,
  IntegrationKindToken,
} from '../../../client/integrations-client.module';
import { Destroyable, HttpErrorHandlerService, takeUntilDestroyed } from '../../../common';
import { IntegrationEventActions } from '../../../common/events/events';
import { ErrorMessagesWrapperComponent } from '../../../misc/error-messages-wrapper/error-messages-wrapper.component';
import { GFEA_HELP_ARTICLE_URL } from '../constants';
import { GfeaApiService } from '../gfea-api';
import {
  GfeaPostingFormComponent,
  initPostingForm,
  postingFormValue,
} from '../gfea-posting-form/gfea-posting-form.component';
import type { EditPostingForm, Offer, Posting } from '../types';

type DialogData = {
  posting: Posting;
  offer: Offer;
};

@Component({
  selector: 'rti-gfea-edit-posting-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gfea-edit-posting-dialog.component.html',
  styleUrls: ['./gfea-edit-posting-dialog.component.less'],
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    ErrorMessagesWrapperComponent,
    GfeaPostingFormComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class GfeaEditPostingDialogComponent implements DialogComponentI<DialogData, boolean> {
  private readonly i18n = inject(I18nService);
  private readonly dialogService = inject(DialogService);
  private readonly httpErrorHandlerService = inject(HttpErrorHandlerService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly gfeaApi = inject(GfeaApiService);
  private readonly store = inject(Store);
  private readonly currentHotspot = inject<OfferPromoteJobBoardsHotspot>(CurrentHotspotToken);
  private readonly integrationKind = inject<IntegrationKinds>(IntegrationKindToken);
  public readonly dialog = inject(DialogRef);

  public posting: Posting | null = null;
  public saving: boolean = false;
  public removing: boolean = false;
  public errors: string[] = [];

  public suggestedOffers: Offer[] | null = null;

  public postingForm: UntypedFormGroup;
  public dialogData: DialogData;

  public GFEA_HELP_ARTICLE_URL = GFEA_HELP_ARTICLE_URL;

  public setDialogData(data: DialogData): void {
    this.dialogData = data;
    this.posting = data.posting;
    this.postingForm = initPostingForm(data.posting);

    this.postingForm.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => this.cdRef.detectChanges());

    this.cdRef.detectChanges();

    this.loadSuggestedOffers();
  }

  public save(): void {
    this.saving = true;
    this.cdRef.detectChanges();

    this.gfeaApi.patch(`posting/${this.posting!.id}`, this.editPostingForm).subscribe(
      (_data: { posting: Posting }) => {
        this.saving = false;
        this.cdRef.detectChanges();

        this.store.dispatch(
          IntegrationEventActions.JobBoardPostingEdited({
            payload: {
              context: { hotspot: this.currentHotspot, integrationKind: this.integrationKind },
            },
          }),
        );

        this.close();
      },
      ({ error }) => {
        this.saving = false;
        this.errors = error.error;
        this.cdRef.detectChanges();
      },
    );
  }

  public remove(): void {
    this.dialogService
      .warning({
        title: this.i18n.translate('rti.job_boards.edit_posting.remove'),
        message: this.i18n.translate('rti.job_boards.edit_posting.remove_confirmation'),
      })
      .pipe(
        filterBoolean,
        tap(() => {
          this.removing = true;
          this.cdRef.detectChanges();
        }),
        switchMap(() => this.gfeaApi.delete(`posting/${this.posting!.id}`)),
        tap(
          () => {
            this.removing = false;
            this.cdRef.detectChanges();

            this.store.dispatch(
              IntegrationEventActions.JobBoardPostingDeleted({
                payload: {
                  context: { hotspot: this.currentHotspot, integrationKind: this.integrationKind },
                },
              }),
            );

            this.dialog.close();
          },
          errorResponse => {
            this.httpErrorHandlerService.default(errorResponse);
            this.removing = false;
            this.cdRef.detectChanges();
          },
        ),
      )
      .subscribe(() => {
        this.close();
      });
  }

  public close(): void {
    this.dialog.close();
  }

  public get formValid(): boolean {
    return this.postingForm.valid;
  }

  public get formDirty(): boolean {
    return this.postingForm.dirty;
  }

  private get editPostingForm(): EditPostingForm {
    return postingFormValue(this.postingForm);
  }

  private loadSuggestedOffers(): void {
    this.gfeaApi.get(`posting/${this.posting!.id}/offers`).subscribe(
      (data: { offers: Offer[] }) => {
        this.suggestedOffers = data.offers.filter(offer => offer.id !== this.dialogData.offer.id);
        this.cdRef.detectChanges();
      },
      errorResponse => {
        this.httpErrorHandlerService.default(errorResponse);
      },
    );
  }
}
